import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  const [location, setLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // True when GPS is unavailable / denied / failed and the user must enter
  // a city (and optionally a street) to determine the initial position.
  const [needsManualLocation, setNeedsManualLocation] = useState(false);

  const requestLocation = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocalizzazione non supportata');
      setNeedsManualLocation(true);
      setLoading(false);
      return false;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes cache
        });
      });

      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      setLocation(coords);
      setNeedsManualLocation(false);

      // Get location name from OpenStreetMap
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&zoom=14`
        );
        const data = await response.json();
        const city = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || '';
        setLocationName(city);
      } catch (e) {
        console.error('Reverse geocoding error:', e);
      }
      return true;
    } catch (err) {
      console.error('Geolocation error:', err);
      if (err.code === 1) {
        setError('Permesso di geolocalizzazione negato');
      } else if (err.code === 2) {
        setError('Posizione non disponibile');
      } else {
        setError('Errore di geolocalizzazione');
      }
      // GPS failed → ask the user to enter city + street manually.
      setNeedsManualLocation(true);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Resolve an address via Nominatim. `city` is required; `street` is
   * optional and refines the result when GPS is unavailable.
   */
  const setManualLocation = useCallback(async (city, street) => {
    const cityTrim = (city || '').trim();
    if (!cityTrim) {
      setError('Inserisci una città');
      return false;
    }
    setLoading(true);
    setError(null);

    try {
      // Prefer structured query when a street is provided (much more accurate).
      const streetTrim = (street || '').trim();
      const url = streetTrim
        ? `https://nominatim.openstreetmap.org/search?format=json&street=${encodeURIComponent(streetTrim)}&city=${encodeURIComponent(cityTrim)}&country=Italia&limit=1`
        : `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityTrim)},Italia&limit=1`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.length > 0) {
        setLocation({
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        });
        // Friendly label: "Via, Città" when both are provided.
        const label = streetTrim
          ? `${streetTrim}, ${cityTrim}`
          : (data[0].display_name?.split(',')[0] || cityTrim);
        setLocationName(label);
        setNeedsManualLocation(false);
        return true;
      }
      // Fallback: if the structured search failed but a street was sent,
      // retry with only the city so the user still gets *some* position.
      if (streetTrim) {
        const fallback = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityTrim)},Italia&limit=1`
        );
        const fb = await fallback.json();
        if (fb.length > 0) {
          setLocation({ lat: parseFloat(fb[0].lat), lng: parseFloat(fb[0].lon) });
          setLocationName(fb[0].display_name?.split(',')[0] || cityTrim);
          setNeedsManualLocation(false);
          return true;
        }
      }
      setError('Località non trovata');
      return false;
    } catch (err) {
      setError('Errore nella ricerca della località');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearLocation = () => {
    setLocation(null);
    setLocationName('');
  };

  // On mount: restore saved location OR auto-try GPS; if GPS fails, the
  // `needsManualLocation` flag becomes true and the UI prompts the user
  // for a city / street.
  useEffect(() => {
    const savedLocation = localStorage.getItem('kyloo_location');
    if (savedLocation) {
      try {
        const parsed = JSON.parse(savedLocation);
        setLocation(parsed.coords);
        setLocationName(parsed.name);
        return;
      } catch (e) {
        console.error('Error parsing saved location:', e);
      }
    }
    // No saved location → try GPS silently. If denied/failed, the
    // `needsManualLocation` flag is set inside requestLocation().
    requestLocation();
  }, [requestLocation]);

  // Save location to localStorage when it changes
  useEffect(() => {
    if (location && locationName) {
      localStorage.setItem('kyloo_location', JSON.stringify({
        coords: location,
        name: locationName
      }));
    }
  }, [location, locationName]);

  const value = {
    location,
    locationName,
    loading,
    error,
    requestLocation,
    setManualLocation,
    clearLocation,
    needsManualLocation,
    dismissManualPrompt: () => setNeedsManualLocation(false),
    hasLocation: !!location
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}
